import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Country codes for location selection
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'JP', name: 'Japan' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IN', name: 'India' }
];

const QueryResearch = () => {
  const [keyword, setKeyword] = useState('');
  const [model, setModel] = useState('gpt-4o-mini-2024-07-18');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  // Location parameters
  const [locationOptions, setLocationOptions] = useState({
    country: 'US',
    city: '',
    region: '',
    timezone: ''
  });

  const handleSearch = async () => {
    if (loading || !keyword.trim()) return;

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const payload = {
        keyword,
        model,
        location: {
          country: locationOptions.country,
          city: locationOptions.city || undefined,
          region: locationOptions.region || undefined,
          timezone: locationOptions.timezone || undefined
        }
      };

      // Remove undefined values
      Object.keys(payload.location).forEach(key => {
        if (payload.location[key] === undefined) {
          delete payload.location[key];
        }
      });

      const response = await api.post('/queries/research', payload);
      setResults(response.data.result);
    } catch (err) {
      setError(err.response?.data?.message || 'Research failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (field, value) => {
    setLocationOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getBadgeVariant = (classification) => {
    switch (classification) {
      case 'brand':
        return 'default';
      case 'competitor':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Query Research</h1>

      <Card>
        <CardHeader>
          <CardTitle>Search for Citation Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter keyword or query"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              className="flex-1"
            />
            <Button
              onClick={handleSearch}
              disabled={loading || !keyword.trim()}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="search-model">Search model</Label>
            <Select value={model} onValueChange={setModel} disabled={loading}>
              <SelectTrigger id="search-model"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gpt-6-luna">GPT-6 Luna · evaluation</SelectItem>
                <SelectItem value="gpt-4.1-mini-2025-04-14">GPT-4.1 mini</SelectItem>
                <SelectItem value="gpt-4o-mini-2024-07-18">GPT-4o mini</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">Each search uses the shared AI allowance. Research results are separate from scheduled keyword measurements.</p>
          </div>
          {/* Location Options Accordion */}
          <Accordion type="single" collapsible>
            <AccordionItem value="location">
              <AccordionTrigger>Location Options (Optional)</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select
                      value={locationOptions.country}
                      onValueChange={(value) => handleLocationChange('country', value)}
                      disabled={loading}
                    >
                      <SelectTrigger id="country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City (Optional)</Label>
                    <Input
                      id="city"
                      placeholder="e.g. London, New York"
                      value={locationOptions.city}
                      onChange={(e) => handleLocationChange('city', e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Region/State (Optional)</Label>
                    <Input
                      id="region"
                      placeholder="e.g. California, London"
                      value={locationOptions.region}
                      onChange={(e) => handleLocationChange('region', e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone (Optional)</Label>
                    <Input
                      id="timezone"
                      placeholder="e.g. America/New_York"
                      value={locationOptions.timezone}
                      onChange={(e) => handleLocationChange('timezone', e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Location settings help refine search results to be more geographically relevant.
                  Leave fields blank to use default global search.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {results && (
        <div className="space-y-6">
          <Alert><AlertDescription>
            Model: {results.model} · Baseline: {results.measurementVersion} · Estimated cost: ${Number(results.usage?.estimatedCostUsd || 0).toFixed(4)} · Duration: {(results.durationMs / 1000).toFixed(1)}s · Tokens: {results.usage?.totalTokens}
          </AlertDescription></Alert>
          <Card>
            <CardHeader>
              <CardTitle>Answer</CardTitle>
              <CardDescription>{results.keyword}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{results.rawOutput}</p>
              <p className="mt-3 text-xs text-muted-foreground">Open the cited pages below to check the sources supporting this answer.</p>
            </CardContent>
          </Card>
          {/* Location Info */}
          {locationOptions.country !== 'US' || locationOptions.city || locationOptions.region || locationOptions.timezone ? (
            <Alert>
              <AlertDescription>
                <strong>Search Location:</strong> {COUNTRIES.find(c => c.code === locationOptions.country)?.name}
                {locationOptions.city && `, ${locationOptions.city}`}
                {locationOptions.region && `, ${locationOptions.region}`}
                {locationOptions.timezone && ` (${locationOptions.timezone})`}
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Summary Stats */}
          <div className="flex justify-center">
            <Card className="w-full max-w-sm">
              <CardContent className="text-center pt-6">
                <p className="text-muted-foreground mb-2">Total Citations</p>
                <p className="text-4xl font-bold text-primary">{results.totalCitations}</p>
              </CardContent>
            </Card>
          </div>

          {/* Citations Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Citations Found</CardTitle>
            </CardHeader>
            <CardContent>
              {results.citations && results.citations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left side - Domain Chart */}
                  <div className="flex flex-col items-center">
                    <h3 className="text-lg font-semibold mb-4">Citations by Domain</h3>
                    <div className="w-full max-w-sm">
                      {(() => {
                        // Group citations by domain
                        const domainCounts = {};
                        results.citations.forEach(citation => {
                          domainCounts[citation.domain] = (domainCounts[citation.domain] || 0) + 1;
                        });

                        const pieData = Object.entries(domainCounts)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 8) // Top 8 domains
                          .map(([domain, count]) => ({
                            name: domain,
                            value: count,
                            percentage: Math.round((count / results.citations.length) * 100)
                          }));

                        const colors = ['#2196f3', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#ffeb3b', '#795548'];

                        return (
                          <div className="flex flex-col gap-2">
                            {pieData.map((item, index) => (
                              <div key={item.name} className="flex items-center gap-2">
                                <div
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: colors[index % colors.length] }}
                                />
                                <span className="text-sm flex-1">{item.name}</span>
                                <span className="text-sm text-muted-foreground">
                                  {item.value} ({item.percentage}%)
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Right side - Citations List */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Top Cited Pages</h3>
                    <div className="max-h-[400px] overflow-auto space-y-2">
                      {results.citations.map((citation, index) => (
                        <Card key={index} variant="outline">
                          <CardContent className="p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-sm text-muted-foreground min-w-[24px]">
                                #{citation.position}
                              </span>
                              <div className="flex-1 min-w-0">
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium hover:underline block mb-1"
                                >
                                  {citation.title}
                                </a>
                                <p className="text-xs text-muted-foreground break-all leading-tight">
                                  {citation.url}
                                </p>
                                {citation.snippet && <p className="mt-2 text-xs leading-relaxed">{citation.snippet}</p>}
                              </div>
                              <Badge variant={getBadgeVariant(citation.brandClassification)}>
                                {citation.brandClassification}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No citations found for this query.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QueryResearch;
